import React, { useEffect, useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

const GlobalDetailDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  fields = [],
  sections = [],
}) => {
  const [copiedMap, setCopiedMap] = useState({});

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(String(text));
    setCopiedMap((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => setCopiedMap((prev) => ({ ...prev, [key]: false })), 2000);
  };

  const renderField = (field, idx) => {
    const fieldKey = `${field.label || "no-label"}-${idx}`;
    const isCopied = copiedMap[fieldKey];

    if (!field.label) {
      return (
        <div key={fieldKey} className="px-4 py-6 border-b border-border last:border-0">
          {field.value}
        </div>
      );
    }

    return (
      <div
        key={fieldKey}
        className="flex items-start gap-3 px-4 py-2.5 even:bg-background odd:bg-surface border-b border-border last:border-0"
      >
        <div className="flex items-center gap-2 w-32 flex-shrink-0">
          {field.icon && <span className="text-text_muted">{field.icon}</span>}
          <span className="text-xs font-semibold text-text_muted uppercase tracking-wide">
            {field.label}
          </span>
        </div>

        <div className="flex-1 text-xs text-text_primary break-words">
          {field.value ?? '—'}
        </div>

        {field.copyable && field.value && (
          <button
            onClick={() => copyToClipboard(field.value, fieldKey)}
            className={[
              "p-1.5 rounded-md transition-all duration-200",
              isCopied
                ? "text-success bg-success_bg"
                : "text-text_muted hover:text-primary hover:bg-background",
            ].join(" ")}
            title={isCopied ? 'Copied!' : `Copy ${field.label}`}
          >
            {isCopied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed inset-y-0 right-0 z-[199] w-full max-w-lg bg-surface shadow-2xl flex flex-col h-full overflow-y-auto slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-surface border-b border-border px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon && <div className="text-primary">{icon}</div>}
            <div>
              <h2 className="text-lg font-bold text-text_primary">{title}</h2>
              {subtitle && <p className="text-xs text-text_muted mt-0.5">{subtitle}</p>}
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-full text-text_muted hover:bg-background hover:text-text_primary transition"
            aria-label="Close drawer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-6 flex-1">
          {sections.length > 0 ? (
            sections.map((section, idx) => (
              <div key={idx}>
                {section.title && (
                  <h3 className="text-xs font-semibold text-text_muted uppercase tracking-wider mb-3">
                    {section.title}
                  </h3>
                )}
                <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
                  {section.fields.map(renderField)}
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
              {fields.map(renderField)}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GlobalDetailDrawer;
